import React from 'react';
import '../../../styles/BulkUpdateFileDrop.css';
import {read} from 'xlsx';
import FileFormatter from "../../FileFormatters/FileFormatter";
import BulkMemberUpdater from "./BulkMemberUpdater";
import Autocomplete from "@mui/material/Autocomplete";
import {GlobalConstants} from "../../../globals/GlobalConstants";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import BulkGroupImporter from "./BulkGroupImporter";
import BulkIDCodeImporter from "./BulkIDCodeImporter";
import BulkPremRateImporter from "./BulkPremRateImporter";
import BulkEDICodeMapImporter from "./BulkEDICodeMapImporter";
import BulkClaimRepriceRemoveImporter from "./BulkClaimRepriceRemoveImporter";
import BulkDivisionCopier from "./BulkDivisionCopier";
import BulkPlanImporter from "./BulkPlanImporter";
import BulkPlanBuildImporter from "./BulkPlanBuildImporter";
import BulkPlanYearPut from "./BulkPlanYearPut";
import BulkExCodePut from "./BulkExCodePut";

function BulkFileUpdateDrop() {
    //State is used here to manipulate what is displayed by the UI while dragging a file and what renders in response to the drop
    const [dragging, setDragging] = React.useState(false);
    const [excelCensus , setCensus] = React.useState({});
    const [excelGroups , setGroups] = React.useState({});
    const [excelIDCodes , setIDCodes] = React.useState({});
    const [excelPremRates , setPremRates] = React.useState({});
    const [excelEDICodeMaps , setEDICodeMaps] = React.useState({});
    const [excelClaimRepriceRemoves , setClaimRepriceRemoves] = React.useState({});
    const [excelDivisionCopies , setDivisionCopies] = React.useState({});
    const [excelPlans , setPlans] = React.useState({});
    const [excelPlanBuild , setPlanBuild] = React.useState({});
    const [excelPlanYears , setPlanYears] = React.useState({});
    const [excelExCodes , setExCodes] = React.useState({});
    const [currentDB, setCurrentDB] = React.useState("CoastalAdminTest");

    const onDBChange = React.useCallback((e: any, newDB: any) => setCurrentDB(newDB.id), []);


    //Values used for detecting hover state and applying effects as needed
    const drop = React.useRef<any>(null);
    const drag = React.useRef(null);

    let text : string = "";
    //we call use effect to allow responses from external factors-in this case the file being dragged
    React.useEffect(() => {

        //Stops default when dragging file
        const handleDragOver = (e: any) => {
            e.preventDefault();
            e.stopPropagation();
        };

        //When dropping a file this updates the state for UI rendering, checks for file data, and sends if found
        //Should add file type validation here, will do later
        const handleDrop = async (e: any) => {
            e.preventDefault();
            e.stopPropagation();

            setDragging(false);

                const rawFiles = await e.dataTransfer.files[0];
                const files = await rawFiles.arrayBuffer();
                const fileName = await rawFiles.name;
                //console.log(files);
                //console.log(e.dataTransfer.files[0]);

                if(await rawFiles.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
                    const wb: any = read(files, {sheetStubs: true});

                    //on grabbing a file from drag and drop trim the .xlsx from the name and store it
                    wb.FileName = fileName.substring(0, fileName.length - 5).toUpperCase();
                    //console.log(files, "UPLOAD");
                    //use state to set the data variable and trigger a rerender so the formatter fires again with the new data. Split apart to reduce unnecessary rendering
                    if(wb.Sheets.Census){
                        setCensus(wb);
                    }
                    else if(wb.Sheets.Groups){
                        setGroups(wb);
                    }
                    else if(wb.Sheets.IDCodeValue){
                        setIDCodes(wb);
                    }
                    else if(wb.Sheets.PremRates){
                        setPremRates(wb);
                    }
                    else if(wb.Sheets.EDICodeMap){
                        setEDICodeMaps(wb);
                    }
                    else if(wb.Sheets.ClaimRepriceRemove){
                        setClaimRepriceRemoves(wb);
                    }
                    else if(wb.Sheets.DivisionCopy){
                        setDivisionCopies(wb);
                    }
                    else if(wb.Sheets.Plans){
                        setPlans(wb);
                    }
                    else if(wb.Sheets.PlanBuild || wb.Sheets.Tiers || wb.Sheets.PlanBenefit ){
                        setPlanBuild(wb);
                    }
                    else if(wb.Sheets.PlanYears){
                        setPlanYears(wb);
                    }
                    else if(wb.Sheets.ExCodes){
                        setExCodes(wb);
                    }
                }
                else if (await rawFiles.type === "text/plain"){
                    const reader = new FileReader()
                    reader.onload = async (e) => {
                        if(e.target){
                            text += (e.target.result);
                            console.log(text);
                        }
                    }
                    reader.readAsText(rawFiles);
                }
        };

        //changes UI when dragging file over
        const handleDragEnter = (e: any) => {
            e.preventDefault();
            e.stopPropagation();

            setDragging(true);
        };

        //opposite of above, resets when dragging away from drop area
        const handleDragLeave = (e: any) => {
            e.preventDefault();
            e.stopPropagation();

            if (e.target === drag.current) {
                setDragging(false);
            }
        };

        //setting event listeners
        drop?.current?.addEventListener('dragover', handleDragOver);
        drop?.current?.addEventListener('drop', handleDrop);
        drop?.current?.addEventListener('dragenter', handleDragEnter);
        drop?.current?.addEventListener('dragleave', handleDragLeave);

        return () => {
            drop?.current?.removeEventListener('dragover', handleDragOver);
            drop?.current?.removeEventListener('drop', handleDrop);
            drop?.current?.removeEventListener('dragenter', handleDragEnter);
            // eslint-disable-next-line react-hooks/exhaustive-deps
            drop?.current?.removeEventListener('dragleave', handleDragLeave);
        };
    }, []);

    //return only has hover layout, default is below and displays conditionally based on state to avoid overlapping
    return (
        <div ref={drop}>
            {dragging && (
                <div ref={drag} className='BulkUpdateFileDrop__placeholder'>
                    Drop The File To Create Groups,Divisions, Rates or IDCodes
                    <span
                        role='img'
                        aria-label='emoji'
                        className='area__icon'
                    >
                        &#128221;
                    </span>
                </div>
            )}
            {!dragging ? <DefaultArea/> : null}
            <Stack spacing={5} direction="row" className="importButtonBar">
                <Autocomplete
                    className="dbSelect"
                    size={"small"}
                    disablePortal
                    disableClearable
                    options={GlobalConstants.VBA_DB}
                    sx={{width: 150}}
                    defaultValue={GlobalConstants.VBA_DB[0]}
                    renderInput={(params) => <TextField {...params}  />}
                    onChange={onDBChange}
                />
            </Stack>
            <BulkMemberUpdater excelData={excelCensus} currentDB={currentDB}></BulkMemberUpdater>
            <BulkGroupImporter excelData={excelGroups} currentDB={currentDB}></BulkGroupImporter>
            <BulkIDCodeImporter excelData={excelIDCodes} currentDB={currentDB}></BulkIDCodeImporter>
            <BulkPremRateImporter excelData={excelPremRates} currentDB={currentDB}></BulkPremRateImporter>
            <BulkEDICodeMapImporter excelData={excelEDICodeMaps} currentDB={currentDB}></BulkEDICodeMapImporter>
            <BulkClaimRepriceRemoveImporter excelData={excelClaimRepriceRemoves} currentDB={currentDB}></BulkClaimRepriceRemoveImporter>
            <BulkDivisionCopier excelData={excelDivisionCopies} currentDB={currentDB}></BulkDivisionCopier>
            <BulkPlanImporter excelData={excelPlans} currentDB={currentDB}></BulkPlanImporter>
            <BulkPlanBuildImporter excelData={excelPlanBuild} currentDB={currentDB}></BulkPlanBuildImporter>
            <BulkPlanYearPut excelData={excelPlanYears} currentDB={currentDB}></BulkPlanYearPut>
            <BulkExCodePut excelData={excelExCodes} currentDB={currentDB}></BulkExCodePut>
        </div>
    );
}

export default React.memo(BulkFileUpdateDrop);

//Regular no hover display
const DefaultArea = () => {
    return (
        <div id="defaultArea" className='BulkUpdateFileDrop__area'>
        Drag And Drop Files of Prem Rates, IDCodes, Groups, or Divisions for Bulk Import
            <span
                role='img'
                aria-label='emoji'
                className='area__icon'
            >
                &#128229;
            </span>
        </div>

    )
}